/*
 * A lab that gets a result wrong sends a second message correcting it (OBR.25 = C), and the
 * backend links the two at ingest (KIN-2088): the correction carries `corrected`, the message it
 * replaces carries `superseded`, and each names the other by inbox id.
 *
 * Without that shown, the two sit in the list as unrelated results and a GP can act on the value
 * the lab has already withdrawn. So a replaced result is never hidden — it stays readable, because
 * seeing what changed is the point — but it has to read as replaced wherever it appears.
 *
 * The fields come from GET .../healthlink/common/results, which backs every results tab. The
 * unassigned lists build the same row shape without them, so every check here treats an absent
 * field as "not a correction" rather than as unknown.
 */

export const CORRECTED_RESULT_LABEL = "Corrected";
export const SUPERSEDED_RESULT_LABEL = "Replaced by a corrected result";

export const isCorrectedResult = (result) => result?.corrected === true;

export const isSupersededResult = (result) => result?.superseded === true;

/*
 * The counterpart's inbox id — `id` on a row of this same list. Absent when the lab's correction
 * arrived before the two were linked, or when the result it corrects is not held here: the
 * relationship is still stated in that case, just without anywhere to send the reader.
 */
export const getCorrectedResultId = (result) =>
  isCorrectedResult(result) ? (result?.correctsResultId ?? null) : null;

export const getSupersedingResultId = (result) =>
  isSupersededResult(result) ? (result?.supersededByResultId ?? null) : null;

/*
 * The counterpart row itself, when the page being shown happens to hold it. Results are paged
 * server-side, so a correction and the result it replaces are frequently on different pages —
 * hence a row, when we have one, and null rather than a broken link when we do not.
 */
export const findRelatedResult = (result, results = []) => {
  const relatedId =
    getSupersedingResultId(result) ?? getCorrectedResultId(result);

  if (relatedId === null) return null;

  return results.find((candidate) => candidate?.id === relatedId) ?? null;
};
